import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase.js';
import { useAuth } from '../../lib/auth.js';
import { trpc } from '../../lib/trpc.js';
import { routes } from '../../routes.js';
import { Button, FormField, TextInput } from '../../components/ui/index.js';
import { ScopeWordmark } from '../../components/ScopeWordmark.js';

const signUpSchema = z.object({
  agencyName: z.string().min(1, 'Agency name is required').max(120),
  userName: z.string().min(1, 'Your name is required').max(120),
  email: z.string().email('Enter a valid email').max(255),
  password: z.string().min(8, 'At least 8 characters').max(72, 'Password is too long'),
});

type SignUpFormValues = z.infer<typeof signUpSchema>;

/**
 * Public sign-up for a brand new agency. Not linked from anywhere in-app
 * or on the marketing surface during the early-adopter phase — share the
 * URL directly with people you want in.
 *
 * Submits to agency.signUp (creates Supabase user + Agency + first User),
 * then signs the user in with the password they just provided.
 */
export function SignUpScreen() {
  const navigate = useNavigate();
  const { auth, recovery } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // While we're auto-signing-in after sign-up, render the existing button
  // as still loading so the user doesn't think they need to click again.
  const [autoSigningIn, setAutoSigningIn] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    mode: 'onTouched',
  });

  const signUp = trpc.agency.signUp.useMutation();

  // Drive navigation from the auth state rather than synchronously after
  // signInWithPassword — Supabase's onAuthStateChange fires asynchronously
  // and the protected dashboard route's guard would otherwise see the old
  // anonymous state and bounce us to /login.
  useEffect(() => {
    // A password-recovery session must set a new password before entering the
    // app — route it to the reset screen (matches App.tsx route guards).
    if (recovery) {
      navigate(auth.kind === 'contact' ? routes.portalResetPassword : routes.resetPassword, {
        replace: true,
      });
      return;
    }
    if (auth.kind === 'agency') navigate(routes.dashboard, { replace: true });
    else if (auth.kind === 'contact') navigate(routes.portalDashboard, { replace: true });
  }, [auth.kind, recovery, navigate]);

  const onSubmit = async (values: SignUpFormValues) => {
    setSubmitError(null);
    try {
      await signUp.mutateAsync(values);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-up failed. Try again.';
      setSubmitError(message);
      return;
    }
    // Server created the auth user with email_confirm=true, so we can
    // sign in immediately with the password they just chose. The catch
    // here covers the case where signInWithPassword throws instead of
    // returning {error} — both paths leave the user on /signup with a
    // useful message rather than a silent failure.
    setAutoSigningIn(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      if (error) {
        setAutoSigningIn(false);
        setSubmitError(
          `Account created, but we couldn't sign you in automatically: ${error.message}. Try the Sign in link below.`,
        );
        return;
      }
    } catch {
      setAutoSigningIn(false);
      setSubmitError(`Account created, but signing in failed. Try the Sign in link below.`);
      return;
    }
    // useEffect above will navigate once the auth state actually updates.
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-8 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <ScopeWordmark />
        </div>

        <div className="rounded-lg border border-scope-border bg-white p-6 shadow-scope sm:p-8">
          <h1 className="mb-1 text-2xl font-semibold tracking-tight text-scope-navy">
            Start your agency on Scope
          </h1>
          <p className="mb-6 text-sm text-scope-text-muted">
            Takes about a minute. You'll be the first user — Super Admin — and can invite the rest
            of your team in once you're in.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              label="Agency name"
              htmlFor="agencyName"
              required
              error={errors.agencyName?.message}
            >
              <TextInput
                id="agencyName"
                placeholder="Acme Studio"
                autoComplete="organization"
                {...register('agencyName')}
              />
            </FormField>

            <FormField
              label="Your name"
              htmlFor="userName"
              required
              error={errors.userName?.message}
            >
              <TextInput
                id="userName"
                placeholder="Jane Smith"
                autoComplete="name"
                {...register('userName')}
              />
            </FormField>

            <FormField label="Work email" htmlFor="email" required error={errors.email?.message}>
              <TextInput
                id="email"
                type="email"
                placeholder="jane@acmestudio.com"
                autoComplete="email"
                {...register('email')}
              />
            </FormField>

            <FormField
              label="Password"
              htmlFor="password"
              required
              error={errors.password?.message}
              hint="At least 8 characters"
            >
              <TextInput
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password')}
              />
            </FormField>

            {submitError && (
              <div className="rounded-lg border border-status-red bg-status-red-bg px-3 py-2 text-xs text-status-red">
                {submitError}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={isSubmitting || signUp.isPending || autoSigningIn}
            >
              Create my agency
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-scope-text-muted">
            Already on Scope?{' '}
            <a href={routes.login} className="font-medium text-scope-navy hover:underline">
              Sign in
            </a>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-scope-text-muted">
          Scope &middot; The professional services engagement workspace
        </p>
      </div>
    </div>
  );
}
